const CLOSED_CANCELLED_STATUSES = new Set(["cancelled", "canceled"]);
const CLOSED_COMPLETED_STATUSES = new Set(["completed", "complete", "done"]);

export const parseDeadlineDate = (deadline = {}) => {
  const rawValue =
    deadline?.date ||
    deadline?.startAt ||
    deadline?.endAt ||
    deadline?.createdAt ||
    null;

  if (!rawValue) {
    return null;
  }

  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDeadlineDate = (value) => {
  const parsed = value instanceof Date ? value : new Date(value);

  if (!value || Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const isPastDeadline = (deadline = {}, referenceDate = new Date()) => {
  const parsed = parseDeadlineDate(deadline);
  return Boolean(parsed && parsed < referenceDate);
};

export const getDeadlineDisplayStatus = (
  deadline = {},
  referenceDate = new Date(),
) => {
  const normalizedStatus = String(deadline?.status || "")
    .trim()
    .toLowerCase();

  if (CLOSED_CANCELLED_STATUSES.has(normalizedStatus)) {
    return "cancelled";
  }

  if (deadline?.completed === true || CLOSED_COMPLETED_STATUSES.has(normalizedStatus)) {
    return "completed";
  }

  return isPastDeadline(deadline, referenceDate) ? "past" : "upcoming";
};

export const getDeadlineStatusLabel = (deadline = {}, referenceDate = new Date()) => {
  const displayStatus = getDeadlineDisplayStatus(deadline, referenceDate);

  if (displayStatus === "cancelled") {
    return "Cancelled";
  }

  if (displayStatus === "completed") {
    return "Completed";
  }

  return displayStatus === "past" ? "Past" : "Upcoming";
};

export const shouldShowInUpcomingDeadlines = (
  deadline = {},
  referenceDate = new Date(),
) => {
  const displayStatus = getDeadlineDisplayStatus(deadline, referenceDate);
  return displayStatus === "past" || displayStatus === "upcoming";
};

// Synced provider events drop off the dashboard once they're over, unlike
// native deadlines (which linger as "Past"). Anchor that check to midnight so a
// meeting earlier TODAY still shows -- it's still on the case calendar, and
// having it vanish at lunchtime reads as a broken sync.
export const shouldShowSyncedCalendarDeadline = (
  deadline = {},
  referenceDate = new Date(),
) => {
  const reference =
    referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const startOfDay = new Date(reference);
  startOfDay.setHours(0, 0, 0, 0);

  return getDeadlineDisplayStatus(deadline, startOfDay) === "upcoming";
};

const isSameCalendarDay = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

/**
 * Order the Overview timeline by urgency: missed items, today's items, then
 * future items by proximity. The reference date is injectable for stable tests.
 */
export const sortDeadlineTimelineEntries = (
  entries = [],
  referenceDate = new Date(),
) => {
  const reference = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);

  return [...entries].sort((left, right) => {
    const leftDate = parseDeadlineDate(left);
    const rightDate = parseDeadlineDate(right);
    if (!leftDate || !rightDate) return Number(!leftDate) - Number(!rightDate);

    const leftPast = leftDate < reference && !isSameCalendarDay(leftDate, reference);
    const rightPast = rightDate < reference && !isSameCalendarDay(rightDate, reference);
    if (leftPast !== rightPast) return leftPast ? -1 : 1;
    if (leftPast) return rightDate - leftDate;

    const leftToday = isSameCalendarDay(leftDate, reference);
    const rightToday = isSameCalendarDay(rightDate, reference);
    if (leftToday !== rightToday) return leftToday ? -1 : 1;

    return leftDate - rightDate;
  });
};

// Django rejects any write to an external-calendar-backed row with
// `external_calendar_rows_are_read_only`, and it treats a row as external when
// ANY of source / eventType / status normalizes to `external_calendar`.
// Mirror all three markers here so the UI never offers a control the backend
// will refuse.
export const isExternalCalendarDeadline = (deadline = {}) =>
  [deadline?.source, deadline?.eventType, deadline?.status].some(
    (value) => String(value || "").trim().toLowerCase() === "external_calendar",
  );

// Calendar ingestion deliberately files one provider event onto every case it
// plausibly belongs to, so a shared meeting shows up on each case calendar that
// cares about it. Every copy carries the same `consolidationKey`. Any view that
// spans more than one case must collapse on that key; per-case views must not.
export const getDeadlineConsolidationKey = (deadline = {}) => {
  if (!isExternalCalendarDeadline(deadline)) {
    return null;
  }

  const explicitKey = String(deadline?.consolidationKey || "").trim().toLowerCase();
  if (explicitKey) {
    return explicitKey;
  }

  // Rows written before consolidationKey existed still identify the same event.
  const externalEventId = String(deadline?.externalEventId || "").trim();
  if (!externalEventId) {
    return null;
  }

  return [
    String(deadline?.sourceProvider || "").trim().toLowerCase(),
    String(deadline?.externalCalendarId || "").trim().toLowerCase(),
    externalEventId,
  ].join("|");
};

export const dedupeDeadlinesAcrossCases = (deadlines = []) => {
  const indexByKey = new Map();
  const result = [];

  deadlines.forEach((deadline) => {
    const key = getDeadlineConsolidationKey(deadline);
    if (!key) {
      result.push(deadline);
      return;
    }

    const score = Number(deadline?.matchScore ?? 0) || 0;
    const existingIndex = indexByKey.get(key);

    if (existingIndex === undefined) {
      indexByKey.set(key, result.length);
      result.push({
        ...deadline,
        duplicateCaseIds: deadline?.caseId ? [deadline.caseId] : [],
      });
      return;
    }

    const existing = result[existingIndex];
    const duplicateCaseIds = existing.duplicateCaseIds.includes(deadline?.caseId)
      ? existing.duplicateCaseIds
      : [...existing.duplicateCaseIds, deadline?.caseId].filter(Boolean);

    // Keep the copy filed on the case that matched most strongly.
    result[existingIndex] =
      score > (Number(existing?.matchScore ?? 0) || 0)
        ? { ...deadline, duplicateCaseIds }
        : { ...existing, duplicateCaseIds };
  });

  return result;
};

export const buildDeadlineToggleUpdate = (
  deadline = {},
  referenceDate = new Date(),
) => {
  const nextCompleted = deadline?.completed !== true;

  if (!nextCompleted) {
    return {
      completed: false,
      status: isPastDeadline(deadline, referenceDate) ? "past" : "upcoming",
    };
  }

  return {
    completed: true,
    status: isPastDeadline(deadline, referenceDate) ? "completed" : "cancelled",
  };
};
